import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import JobCodeSection from '../components/JobCodeSection';
import Modal from '../components/Modal';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../context/AuthContext';
import {
  completeElevatorAppointment,
  fetchElevatorAppointment,
  hasOpenConflict,
  rescheduleElevatorAppointment,
  updateElevatorAppointment,
  voidElevatorAppointment,
  type ElevatorAppointmentRow,
} from '../api/elevatorAppointments';
import {
  ELEVATOR_INTAKE_FORM,
  PASS_FAIL_OPTIONS,
  isTableField,
  type FormField,
  type FormSection,
  type SimpleField,
  type TableField,
} from '../data/elevatorIntakeForm';
import './Page.css';
import './Events.css';
import './Elevators.css';

type Values = Record<string, unknown>;
type TableRow = Record<string, string | boolean>;

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v);
}

function asRows(v: unknown): TableRow[] {
  return Array.isArray(v) ? (v as TableRow[]) : [];
}

const ElevatorAppointmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const idNum = id ? parseInt(id, 10) : NaN;

  const [appt, setAppt] = useState<ElevatorAppointmentRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [values, setValues] = useState<Values>({});
  const valuesRef = useRef<Values>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const [actionBusy, setActionBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState('');
  const [resolveOpen, setResolveOpen] = useState(false);
  const [rescheduleAt, setRescheduleAt] = useState('');
  const [resolveError, setResolveError] = useState('');

  const readOnly = appt != null && appt.status !== 'Scheduled';

  const load = useCallback(async () => {
    if (!Number.isInteger(idNum) || idNum < 1) {
      setError('Invalid appointment.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const row = await fetchElevatorAppointment(idNum);
      setAppt(row);
      const cd = (row.checklist_data ?? {}) as Values;
      setValues(cd);
      valuesRef.current = cd;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load appointment.');
      setAppt(null);
    } finally {
      setLoading(false);
    }
  }, [idNum]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const doSave = useCallback(async () => {
    if (!Number.isInteger(idNum)) return;
    setSaveState('saving');
    try {
      await updateElevatorAppointment(idNum, { checklist_data: valuesRef.current });
      setSaveState('saved');
    } catch {
      setSaveState('error');
    }
  }, [idNum]);

  const scheduleSave = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      void doSave();
    }, 700);
  }, [doSave]);

  const setValue = useCallback(
    (key: string, value: unknown) => {
      setValues((prev) => {
        const next = { ...prev, [key]: value };
        valuesRef.current = next;
        return next;
      });
      setSaveState('saving');
      scheduleSave();
    },
    [scheduleSave]
  );

  const handleComplete = async () => {
    if (!appt) return;
    if (!window.confirm('Mark this appointment as completed? The intake form will become read-only.')) return;
    setActionBusy(true);
    try {
      // Flush any pending checklist edits first so nothing is lost.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await updateElevatorAppointment(appt.id, { checklist_data: valuesRef.current });
      const updated = await completeElevatorAppointment(appt.id);
      setAppt(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to complete appointment.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitVoid = async () => {
    if (!appt) return;
    const reason = voidReason.trim();
    if (!reason) {
      setVoidError('Please provide a reason for voiding.');
      return;
    }
    setActionBusy(true);
    setVoidError('');
    try {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await updateElevatorAppointment(appt.id, { checklist_data: valuesRef.current });
      const updated = await voidElevatorAppointment(appt.id, reason);
      setAppt(updated);
      setVoidOpen(false);
      setVoidReason('');
    } catch (e) {
      setVoidError(e instanceof Error ? e.message : 'Failed to void appointment.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!appt) return;
    if (!window.confirm('Reopen this appointment for editing? It will move back to Scheduled.')) return;
    setActionBusy(true);
    try {
      const updated = await updateElevatorAppointment(appt.id, { status: 'Scheduled' });
      setAppt(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reopen appointment.');
    } finally {
      setActionBusy(false);
    }
  };

  const submitReschedule = async () => {
    if (!appt) return;
    if (!rescheduleAt) {
      setResolveError('Please choose a new date and time.');
      return;
    }
    setActionBusy(true);
    setResolveError('');
    try {
      const updated = await rescheduleElevatorAppointment(appt.id, new Date(rescheduleAt).toISOString());
      setAppt(updated);
      setResolveOpen(false);
      setRescheduleAt('');
    } catch (e) {
      setResolveError(e instanceof Error ? e.message : 'Failed to reschedule.');
    } finally {
      setActionBusy(false);
    }
  };

  const dismissConflict = async () => {
    if (!appt) return;
    setActionBusy(true);
    setResolveError('');
    try {
      const updated = await updateElevatorAppointment(appt.id, { resolve_conflict: true });
      setAppt(updated);
      setResolveOpen(false);
    } catch (e) {
      setResolveError(e instanceof Error ? e.message : 'Failed to resolve conflict.');
    } finally {
      setActionBusy(false);
    }
  };

  const saveStatusText = useMemo(() => {
    if (readOnly) return '';
    switch (saveState) {
      case 'saving':
        return 'Saving…';
      case 'saved':
        return 'All changes saved';
      case 'error':
        return 'Could not save changes — check your connection';
      default:
        return '';
    }
  }, [saveState, readOnly]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Appointment" />
            <div className="page-content">
              <p className="page-subtitle">Loading…</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error || !appt) {
    return (
      <div className="page-container">
        <div className="page-layout">
          <Sidebar />
          <main className="page-main">
            <PageHeader title="Appointment" />
            <div className="page-content">
              <p className="create-order-error" role="alert">
                {error || 'Appointment not found.'}
              </p>
              <button type="button" className="page-toolbar-btn" onClick={() => navigate('/elevators')}>
                ← Back to Elevators
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const statusClass =
    appt.status === 'Completed'
      ? 'elevator-status-badge--completed'
      : appt.status === 'Void'
      ? 'elevator-status-badge--void'
      : 'elevator-status-badge--scheduled';

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Elevator appointment" subtitle="Installer intake checklist" />
          <div className="page-content">
            <button type="button" className="elevator-back-link" onClick={() => navigate('/elevators')}>
              ← Back to Elevators
            </button>

            <div className="elevator-detail-title-row">
              <h1 className="project-detail-title" style={{ margin: 0 }}>
                {appt.business_name}
                <span className={`elevator-status-badge ${statusClass}`}>{appt.status}</span>
              </h1>
              <div className="elevator-detail-actions">
                {appt.status === 'Scheduled' ? (
                  <>
                    <button
                      type="button"
                      className="elevator-btn elevator-btn--complete"
                      disabled={actionBusy}
                      onClick={() => void handleComplete()}
                    >
                      {actionBusy ? 'Working…' : 'Complete'}
                    </button>
                    <button
                      type="button"
                      className="elevator-btn elevator-btn--void"
                      disabled={actionBusy}
                      onClick={() => {
                        setVoidReason('');
                        setVoidError('');
                        setVoidOpen(true);
                      }}
                    >
                      Void
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="elevator-btn elevator-btn--reopen"
                    disabled={actionBusy}
                    onClick={() => void handleReopen()}
                  >
                    {actionBusy ? 'Working…' : 'Reopen'}
                  </button>
                )}
              </div>
            </div>

            {appt.status === 'Void' ? (
              <div className="elevator-void-banner" role="status">
                <strong>This appointment was voided.</strong>
                Reason: {appt.void_reason || '—'}
                {appt.voided_at ? ` · ${formatDateTime(appt.voided_at)}` : ''}
              </div>
            ) : null}
            {appt.status === 'Completed' ? (
              <div className="elevator-completed-banner" role="status">
                Completed{appt.completed_at ? ` on ${formatDateTime(appt.completed_at)}` : ''}. The intake record below is
                read-only. Use “Reopen” to make changes.
              </div>
            ) : null}

            {hasOpenConflict(appt) && appt.status === 'Scheduled' ? (
              <div className="appt-conflict-banner" role="alert">
                <span className="appt-conflict-banner-text">
                  <strong>Scheduling conflict reported{appt.conflict_author_name ? ` by ${appt.conflict_author_name}` : ''}:</strong>{' '}
                  {appt.conflict_reason || '—'}
                </span>
                <button
                  type="button"
                  className="appt-conflict-resolve-btn"
                  disabled={actionBusy}
                  onClick={() => {
                    setRescheduleAt('');
                    setResolveError('');
                    setResolveOpen(true);
                  }}
                >
                  Resolve
                </button>
              </div>
            ) : null}

            {/* Business / contact / appointment summary */}
            <JobCodeSection jobCode={appt.job_code} clientId={appt.client_id} />

            <section className="elevator-info-card">
              <div className="elevator-info-grid">
                <InfoItem label="Name of Business" value={appt.business_name} />
                <InfoItem label="Address of Business" value={appt.business_address} />
                <InfoItem label="Point of Contact" value={appt.point_of_contact} />
                <InfoItem label="Contact Phone" value={appt.poc_phone} />
                <InfoItem label="Contact Email" value={appt.poc_email} />
                <InfoItem label="Appointment Date & Time" value={formatDateTime(appt.appointment_at)} />
                <InfoItem label="Created by" value={appt.created_by_username} />
                <InfoItem
                  label="Assigned employees"
                  value={appt.assignees.length > 0 ? appt.assignees.map((a) => a.username).join(', ') : null}
                />
                {appt.task_id != null ? (
                  <div className="elevator-info-item">
                    <span className="elevator-info-label">Task</span>
                    <span className="elevator-info-value">
                      <button type="button" className="elevator-back-link" onClick={() => navigate(`/tasks/${appt.task_id}`)}>
                        View task
                      </button>
                    </span>
                  </div>
                ) : null}
              </div>
            </section>

            {!readOnly ? (
              <p
                className={`elevator-save-status${
                  saveState === 'saving'
                    ? ' elevator-save-status--saving'
                    : saveState === 'saved'
                    ? ' elevator-save-status--saved'
                    : saveState === 'error'
                    ? ' elevator-save-status--error'
                    : ''
                }`}
                role="status"
                aria-live="polite"
              >
                {saveStatusText}
              </p>
            ) : null}

            {ELEVATOR_INTAKE_FORM.map((section) => (
              <SectionView
                key={section.id}
                section={section}
                values={values}
                readOnly={readOnly}
                onChange={setValue}
              />
            ))}
          </div>
        </main>
      </div>

      <Modal isOpen={voidOpen} onClose={() => setVoidOpen(false)} title="Void appointment" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            Please provide a reason for voiding this appointment. It will be shown at the top of the appointment and in the
            Voided Appointments table.
          </p>
          <div className="form-group">
            <label htmlFor="elevator-void-reason" className="form-label">
              Reason
            </label>
            <textarea
              id="elevator-void-reason"
              className="form-input"
              rows={4}
              value={voidReason}
              onChange={(e) => {
                setVoidReason(e.target.value);
                setVoidError('');
              }}
              disabled={actionBusy}
            />
          </div>
          {voidError ? (
            <p className="create-order-error" role="alert">
              {voidError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => setVoidOpen(false)}>
            Cancel
          </button>
          <button type="button" className="elevator-btn elevator-btn--void" disabled={actionBusy} onClick={() => void submitVoid()}>
            {actionBusy ? 'Voiding…' : 'Void appointment'}
          </button>
        </div>
      </Modal>

      <Modal isOpen={resolveOpen} onClose={() => setResolveOpen(false)} title="Resolve scheduling conflict" narrow>
        <div className="modal-body">
          <p style={{ margin: '0 0 1rem', color: '#374151', fontSize: '0.9375rem' }}>
            The client reported a conflict with the current time. Reschedule to a new date/time, void the appointment, or
            mark the conflict as handled.
          </p>
          <div className="form-group">
            <label htmlFor="elevator-reschedule-at" className="form-label">
              New date and time
            </label>
            <input
              id="elevator-reschedule-at"
              type="datetime-local"
              className="form-input"
              value={rescheduleAt}
              onChange={(e) => {
                setRescheduleAt(e.target.value);
                setResolveError('');
              }}
              disabled={actionBusy}
            />
          </div>
          {resolveError ? (
            <p className="create-order-error" role="alert">
              {resolveError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          <button
            type="button"
            className="elevator-btn elevator-btn--void"
            disabled={actionBusy}
            onClick={() => {
              setResolveOpen(false);
              setVoidReason('');
              setVoidError('');
              setVoidOpen(true);
            }}
          >
            Void instead
          </button>
          <button type="button" className="cancel-button" disabled={actionBusy} onClick={() => void dismissConflict()}>
            Mark handled
          </button>
          <button type="button" className="update-button" disabled={actionBusy} onClick={() => void submitReschedule()}>
            {actionBusy ? 'Rescheduling…' : 'Reschedule'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

function InfoItem({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="elevator-info-item">
      <span className="elevator-info-label">{label}</span>
      <span className="elevator-info-value">{value && value.trim() ? value : '—'}</span>
    </div>
  );
}

function SectionView({
  section,
  values,
  readOnly,
  onChange,
}: {
  section: FormSection;
  values: Values;
  readOnly: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  return (
    <section className="elevator-form-section">
      <h2 className="elevator-form-section-title">
        {section.number ? <span className="elevator-form-section-number">{section.number}</span> : null}
        {section.title}
      </h2>
      {section.description ? <p className="elevator-form-section-desc">{section.description}</p> : null}
      {section.groups.map((group, gi) => (
        <div className="elevator-form-group" key={gi}>
          {group.title ? <h3 className="elevator-form-group-title">{group.title}</h3> : null}
          <div className={group.layout === 'grid' ? 'elevator-grid' : group.layout === 'checks' ? 'elevator-checks' : 'elevator-stack'}>
            {group.fields.map((field) => (
              <FieldView key={field.id} field={field} values={values} readOnly={readOnly} onChange={onChange} />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}

function FieldView({
  field,
  values,
  readOnly,
  onChange,
}: {
  field: FormField;
  values: Values;
  readOnly: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  if (isTableField(field)) {
    return <TableFieldView field={field} values={values} readOnly={readOnly} onChange={onChange} />;
  }
  const sf = field as SimpleField;
  const raw = values[sf.id];

  if (sf.type === 'checkbox') {
    const checked = raw === true;
    const notesVal = asString(values[`${sf.id}_notes`]);
    return (
      <div className={sf.notes ? 'elevator-check-row' : ''}>
        <label className="elevator-check">
          {readOnly ? (
            <span className={`elevator-readonly-check${checked ? ' elevator-readonly-check--yes' : ''}`}>
              {checked ? '☑' : '☐'}
            </span>
          ) : (
            <input type="checkbox" checked={checked} onChange={(e) => onChange(sf.id, e.target.checked)} />
          )}
          <span>{sf.label}</span>
        </label>
        {sf.notes ? (
          readOnly ? (
            notesVal ? (
              <span className="elevator-check-notes elevator-readonly-value">Notes: {notesVal}</span>
            ) : null
          ) : (
            <input
              type="text"
              className="elevator-notes-input elevator-check-notes"
              placeholder="Notes"
              value={notesVal}
              onChange={(e) => onChange(`${sf.id}_notes`, e.target.value)}
            />
          )
        ) : null}
      </div>
    );
  }

  if (sf.type === 'passfail') {
    const val = asString(raw);
    return (
      <div className="elevator-passfail-row">
        <span className="elevator-passfail-label">{sf.label}</span>
        {readOnly ? (
          <span className={`elevator-readonly-value${val ? '' : ' elevator-readonly-value--empty'}`}>{val || '—'}</span>
        ) : (
          <span className="elevator-passfail-options">
            {PASS_FAIL_OPTIONS.map((opt) => (
              <label key={opt}>
                <input
                  type="radio"
                  name={sf.id}
                  checked={val === opt}
                  onChange={() => onChange(sf.id, val === opt ? '' : opt)}
                  onClick={() => {
                    if (val === opt) onChange(sf.id, '');
                  }}
                />
                {opt}
              </label>
            ))}
          </span>
        )}
      </div>
    );
  }

  // text / textarea
  const val = asString(raw);
  return (
    <div>
      <label className="elevator-field-label" htmlFor={`fld-${sf.id}`}>
        {sf.label}
      </label>
      {readOnly ? (
        <div className={`elevator-readonly-value${val ? '' : ' elevator-readonly-value--empty'}`} style={{ whiteSpace: 'pre-wrap' }}>
          {val || '—'}
        </div>
      ) : sf.type === 'textarea' ? (
        <textarea
          id={`fld-${sf.id}`}
          className="elevator-text-input elevator-textarea"
          value={val}
          onChange={(e) => onChange(sf.id, e.target.value)}
        />
      ) : (
        <input
          id={`fld-${sf.id}`}
          type="text"
          className="elevator-text-input"
          value={val}
          onChange={(e) => onChange(sf.id, e.target.value)}
        />
      )}
    </div>
  );
}

function TableFieldView({
  field,
  values,
  readOnly,
  onChange,
}: {
  field: TableField;
  values: Values;
  readOnly: boolean;
  onChange: (key: string, value: unknown) => void;
}) {
  const isFixed = Array.isArray(field.fixedRows) && field.fixedRows.length > 0;
  const rows = asRows(values[field.id]);

  // Signature capture happens in a modal, launched per cell via a "Sign" button.
  const [signTarget, setSignTarget] = useState<{ rowIndex: number; colKey: string; label: string } | null>(null);
  const [signDraft, setSignDraft] = useState('');

  const displayRows: TableRow[] = isFixed
    ? field.fixedRows!.map((_, i) => rows[i] ?? {})
    : rows;

  const updateCell = (rowIndex: number, colKey: string, value: string | boolean) => {
    const base = isFixed ? field.fixedRows!.map((_, i) => rows[i] ?? {}) : [...rows];
    base[rowIndex] = { ...(base[rowIndex] ?? {}), [colKey]: value };
    onChange(field.id, base);
  };

  const addRow = () => {
    onChange(field.id, [...rows, {}]);
  };
  const removeRow = (rowIndex: number) => {
    const next = rows.filter((_, i) => i !== rowIndex);
    onChange(field.id, next);
  };

  const renderCell = (row: TableRow, rowIndex: number, col: TableField['columns'][number]) => {
    const cellVal = row[col.key];
    if (col.type === 'checkbox') {
      const checked = cellVal === true;
      if (readOnly) return <span className={checked ? 'elevator-readonly-check--yes' : ''}>{checked ? '☑' : '☐'}</span>;
      return <input type="checkbox" checked={checked} onChange={(e) => updateCell(rowIndex, col.key, e.target.checked)} />;
    }
    if (col.type === 'passfail') {
      const v = asString(cellVal);
      if (readOnly) return <span>{v || '—'}</span>;
      return (
        <select value={v} onChange={(e) => updateCell(rowIndex, col.key, e.target.value)} className="elevator-table-select">
          <option value="">—</option>
          {PASS_FAIL_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    }
    if (col.type === 'signature') {
      const v = asString(cellVal);
      if (readOnly) {
        return v ? (
          <img src={v} alt="Signature" className="signature-pad-image" style={{ maxWidth: 180, maxHeight: 60 }} />
        ) : (
          <span className="signature-pad-empty">—</span>
        );
      }
      const rowLabel = isFixed ? field.fixedRows![rowIndex] : `Row ${rowIndex + 1}`;
      return (
        <div className="elevator-sign-cell">
          {v ? (
            <img src={v} alt="Signature" className="signature-pad-image" style={{ maxWidth: 160, maxHeight: 48 }} />
          ) : (
            <span className="signature-pad-empty">Not signed</span>
          )}
          <button
            type="button"
            className="elevator-sign-btn"
            onClick={() => {
              setSignDraft(v);
              setSignTarget({ rowIndex, colKey: col.key, label: rowLabel });
            }}
          >
            {v ? 'Re-sign' : 'Sign'}
          </button>
        </div>
      );
    }
    const v = asString(cellVal);
    if (readOnly) return <span>{v || '—'}</span>;
    return <input type="text" value={v} onChange={(e) => updateCell(rowIndex, col.key, e.target.value)} />;
  };

  return (
    <div className="elevator-table-wrap">
      <table className="elevator-table">
        <thead>
          <tr>
            {isFixed ? <th>{field.firstColLabel ?? ''}</th> : null}
            {field.columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            {!isFixed && !readOnly ? <th aria-label="Remove" /> : null}
          </tr>
        </thead>
        <tbody>
          {displayRows.length === 0 && !isFixed ? (
            <tr>
              <td colSpan={field.columns.length + 1} className="events-empty-cell">
                No rows yet.
              </td>
            </tr>
          ) : (
            displayRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {isFixed ? <td className="elevator-table-rowlabel">{field.fixedRows![rowIndex]}</td> : null}
                {field.columns.map((col) => (
                  <td key={col.key}>{renderCell(row, rowIndex, col)}</td>
                ))}
                {!isFixed && !readOnly ? (
                  <td>
                    <button type="button" className="elevator-table-remove" aria-label="Remove row" onClick={() => removeRow(rowIndex)}>
                      ×
                    </button>
                  </td>
                ) : null}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!isFixed && !readOnly ? (
        <button type="button" className="elevator-table-add" onClick={addRow}>
          + {field.addRowLabel ?? 'Add row'}
        </button>
      ) : null}

      <Modal
        isOpen={signTarget != null}
        onClose={() => setSignTarget(null)}
        title={signTarget ? `Sign — ${signTarget.label}` : 'Sign'}
        narrow
      >
        <div className="modal-body">
          <p style={{ margin: '0 0 0.75rem', color: '#374151', fontSize: '0.9375rem' }}>
            Draw the signature below, then save.
          </p>
          <SignaturePad value={signDraft} onChange={setSignDraft} width={420} height={170} ariaLabel="Signature" />
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" onClick={() => setSignTarget(null)}>
            Cancel
          </button>
          <button
            type="button"
            className="update-button"
            onClick={() => {
              if (signTarget) updateCell(signTarget.rowIndex, signTarget.colKey, signDraft);
              setSignTarget(null);
            }}
          >
            Save signature
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default ElevatorAppointmentDetail;
